import { DEMO_PEOPLE } from '@/lib/demo/people'
import type { CreditGrant } from '@/api/customersApi'
import type { GrantCreditInput } from '@/features/actions/schemas/actionSchemas'
import type { CreateCustomerInput } from '@/features/customers/schemas/customerSchema'
import type {
  Customer,
  CustomerActivity,
  CustomerDetail,
  CustomerDocument,
  CustomerNote,
  CustomerPage,
  CustomerQuery,
  Treatment,
  TreatmentState,
} from '@/features/customers/types'
import { ApiError } from '@/api/errors'
import { demoBilling } from '@/features/actions/demo/demoBilling'
import type { Credits } from '@/types/models'

/**
 * The customer book, seeded from the prototype's own mock generator so the page
 * looks like the design while there is no backend.
 *
 * The full profile is the record we keep; the list is a projection of it, which
 * is how the real endpoints are shaped too.
 */

const NAMES = DEMO_PEOPLE

const CITIES = ['Fellbach', 'Stuttgart']
const PLANS = ['Bodyshaping', 'Fatburning', 'Lymphdrainage']
const MANAGERS = ['Anna Müller', 'Linda Meier']
const ACTIVITY_KINDS = ['Anruf', 'E-Mail', 'Meeting', 'Signatur']
const BODY_TYPES = ['Sanduhr', 'Apfel', 'Birne', 'Rechteck']
const MACHINES = ['Basic 1', 'Basic 2', 'Premium 1']
const THERAPISTS = ['L. Dinh', 'Administrator']
const STATES: TreatmentState[] = ['done', 'check_in', 'done', 'booked', 'cancelled', 'no_show']

/** What one treatment is worth, for the revenue shown on a card. */
const TREATMENT_PRICE = 99

/** Only the first few customers have been in — the rest show the empty plan. */
const CUSTOMERS_WITH_TREATMENTS = 8

type CompleteCustomer = Customer & CustomerDetail

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

/** Contract stages, so a demo card lands in the same column a live one would. */
const DEMO_STAGES = [
  { stage: 'new', stageLabel: 'Neu', stageSection: 'outreach' },
  { stage: 'recontact', stageLabel: 'Erneute Kontaktaufnahme', stageSection: 'outreach' },
  { stage: 'trial_booked', stageLabel: 'Probebehandlung gebucht', stageSection: 'trial' },
  { stage: 'trial_attended', stageLabel: 'Probebehandlung stattgefunden', stageSection: 'trial' },
  { stage: 'follow_up', stageLabel: 'Nachfassen', stageSection: 'trial' },
  { stage: 'won_active', stageLabel: 'Gewonnen / Aktiv', stageSection: 'active_customer' },
  { stage: 'exited', stageLabel: 'Ausgeschieden', stageSection: 'closed' },
] as const

const DEMO_ORIGINS = [
  { origin: 'website', originLabel: 'Website' },
  { origin: 'referral', originLabel: 'Empfehlung' },
  { origin: 'social_media', originLabel: 'Social Media' },
] as const

function treatmentsFor(index: number, id: string): Treatment[] {
  if (index >= CUSTOMERS_WITH_TREATMENTS) return []

  return Array.from({ length: 3 + (index % 3) }, (_, slot) => {
    const step = index + slot
    return {
      id: `${id}-t${slot + 1}`,
      date: `2026-07-${pad(2 + ((step * 3) % 20))}`,
      start: `${pad(9 + (step % 8))}:00`,
      end: `${pad(10 + (step % 8))}:00`,
      machine: MACHINES[step % MACHINES.length],
      therapist: THERAPISTS[step % THERAPISTS.length],
      program: PLANS[step % PLANS.length],
      state: STATES[step % STATES.length],
    }
  }).sort((a, b) => `${b.date}${b.start}`.localeCompare(`${a.date}${a.start}`))
}

function documentsFor(index: number, id: string, name: string): CustomerDocument[] {
  const invoice = `INV/2026/${String(index + 1).padStart(5, '0')}`

  return [
    {
      id: `${id}-d1`,
      name: `Anamnesebogen – ${name}.pdf`,
      kind: 'consent',
      created: '02.07.2026',
      internal: false,
    },
    {
      id: `${id}-d2`,
      name: `Rechnung ${invoice}.pdf`,
      kind: 'invoice',
      created: '08.07.2026',
      internal: false,
    },
    // The odd customer also has an internal-only consent on file.
    ...(index % 3 === 0
      ? [
          {
            id: `${id}-d3`,
            name: `Einwilligung Fotodokumentation – ${name}.pdf`,
            kind: 'consent' as const,
            created: '02.07.2026',
            internal: true,
          },
        ]
      : []),
  ]
}

function notesFor(index: number, id: string): CustomerNote[] {
  return [
    {
      id: `${id}-n1`,
      author: 'Admin',
      at: 'Heute, 10:01 · Intern',
      body:
        index % 3 === 0
          ? 'Wünscht Termine bevorzugt am Nachmittag. Sensibel auf kurzfristige Terminänderungen.'
          : 'Keine besonderen Hinweise.',
    },
    {
      id: `${id}-n2`,
      author: 'Linda Meier',
      at: '08.07.2026 · Intern',
      body: 'Positive Entwicklung. Nächste Kontrolle nach fünf Sitzungen.',
    },
  ]
}

function activitiesFor(index: number, id: string, name: string): CustomerActivity[] {
  const invoice = `INV/2026/${String(index + 1).padStart(5, '0')}`

  return [
    {
      id: `${id}-a1`,
      kind: 'message',
      title: 'Terminbestätigung gesendet',
      detail: 'Automatische Nachricht über Superchat',
      at: 'Heute, 09:16',
    },
    {
      id: `${id}-a2`,
      kind: 'document',
      title: 'Rechnung als PDF angehängt',
      detail: invoice,
      at: '08.07.2026',
    },
    {
      id: `${id}-a3`,
      kind: 'document',
      title: 'Anamnesebogen signiert',
      detail: `Anamnesebogen – ${name}.pdf`,
      at: '02.07.2026',
    },
    {
      id: `${id}-a4`,
      kind: 'system',
      title: 'Kontakt erstellt',
      detail: 'External Lead Gateway',
      at: '01.07.2026',
    },
  ]
}

function seed(): CompleteCustomer[] {
  return NAMES.map((name, index) => {
    const id = String(500 + index)
    const handle = name
      .toLowerCase()
      .replace(/[’'\s.-]/g, '')
      .slice(0, 12)
    const packageTag =
      index % 5 === 0 ? 'Premium-Paket' : index % 3 === 0 ? 'Basic-Paket' : undefined
    const phone = `+49 17${(560000000 + index * 1937).toString().slice(-8)}`
    const city = CITIES[index % CITIES.length]

    return {
      id,
      name,
      firstName: name.split(' ')[0],
      lastName: name.split(' ').slice(1).join(' ') || name,
      createdAt: new Date(Date.UTC(2026, 0, 1 + index)).toISOString(),
      email: `${handle}${index + 1}@gmx.net`,
      phone,
      mobile: phone,
      city,
      country: 'Deutschland',
      language: 'Deutsch',
      company: `Abnehmen im Liegen ${city}`,
      tags: [index % 4 === 0 ? 'WIX Website' : 'Empfehlung', packageTag].filter(
        Boolean,
      ) as string[],
      segment: index % 7 === 0 ? 'M40' : 'Standard',
      // The same funnel fields `GET /api/customers` sends, using contract codes
      // so demo mode groups into the same eleven board columns as the real one.
      ...DEMO_STAGES[index % DEMO_STAGES.length],
      origin: DEMO_ORIGINS[index % DEMO_ORIGINS.length].origin,
      originLabel: DEMO_ORIGINS[index % DEMO_ORIGINS.length].originLabel,
      attemptCount: index % 4,
      nextActionAt: null,
      dueToday: index % 3 === 0,
      bodyType: BODY_TYPES[index % BODY_TYPES.length],
      programPlan: index % 5 === 0 ? 'Lipödem' : PLANS[index % PLANS.length],
      documentCount: 0,
      manager: MANAGERS[index % MANAGERS.length],
      lastActivityKind: ACTIVITY_KINDS[index % ACTIVITY_KINDS.length],
      openActivities: (index % 5) + 1,
      lastTreatment: `2026-07-${pad(2 + (index % 20))}`,
      saleCount: 2,
      signatureCount: 1,
      anamnesisSigned: true,
      treatments: treatmentsFor(index, id),
      documents: documentsFor(index, id, name),
      notes: notesFor(index, id),
      activities: activitiesFor(index, id, name),
    } satisfies CompleteCustomer
  }).map((customer) => ({ ...customer, documentCount: customer.documents.length }))
}

const customers = seed()

/** The list carries what a card shows; the profile carries the rest. */
function toListItem(customer: CompleteCustomer): Customer {
  const {
    mobile: _mobile,
    country: _country,
    language: _language,
    bodyType: _bodyType,
    company: _company,
    lastTreatment: _lastTreatment,
    saleCount: _saleCount,
    signatureCount: _signatureCount,
    anamnesisSigned: _anamnesisSigned,
    treatments: _treatments,
    documents: _documents,
    notes: _notes,
    activities: _activities,
    ...listItem
  } = customer
  return listItem
}

/** The balance the book and the card show, read from the billing ledger. */
function balanceOf(credits: Credits) {
  return {
    purchased: credits.purchased,
    remaining: credits.available,
    revenue: credits.consumed * TREATMENT_PRICE,
  }
}

/**
 * The Kundenkarte's blocks, derived from the record so they agree with the
 * list. The newest customer never entered the funnel, so demo mode shows the
 * `pipeline: null` case the real card has to handle.
 */
function withCardBlocks(customer: CompleteCustomer): CompleteCustomer {
  const credits = demoBilling.creditsOf(customer.id)
  const newest = customers.reduce((max, candidate) => Math.max(max, Number(candidate.id)), 0)
  const outsideFunnel = Number(customer.id) === newest

  return {
    ...customer,
    ...balanceOf(credits),
    origin: outsideFunnel ? undefined : customer.origin,
    originLabel: outsideFunnel ? undefined : customer.originLabel,
    pipeline:
      outsideFunnel || !customer.stage
        ? null
        : {
            stage: customer.stage,
            stageLabel: customer.stageLabel ?? customer.stage,
            stageSection: customer.stageSection ?? 'outreach',
            attemptCount: customer.attemptCount ?? 0,
            attemptsToday: 0,
            blockedForToday: false,
            dueToday: customer.dueToday ?? false,
            nextActionAt: customer.nextActionAt ?? null,
            restPhaseUntil: null,
            salesProcessClosed: false,
            manualOverride: false,
          },
    credits,
    appointments: [],
    recentCalls: [
      {
        id: `${customer.id}-c2`,
        occurredAt: '2026-07-14T10:05:00+02:00',
        outcome: 'schedule_callback',
        outcomeLabel: 'Rückruf vereinbart',
        byUserName: 'Admin',
        note: 'Möchte nach dem Urlaub zurückgerufen werden.',
      },
      {
        id: `${customer.id}-c1`,
        occurredAt: '2026-07-10T16:40:00+02:00',
        outcome: 'not_reached',
        outcomeLabel: 'Nicht erreicht',
        byUserName: 'Admin',
        note: null,
      },
    ],
  }
}

function find(customerId: string): CompleteCustomer {
  const customer = customers.find((candidate) => candidate.id === customerId)
  if (!customer) throw new ApiError({ message: 'Dieser Kunde wurde nicht gefunden.', status: 404 })
  return customer
}

export const demoCustomers = {
  /** Mirrors the server: substring search, stage filter, last-name order, zero-based pages. */
  fetchPage: async ({ q, stage, page = 0, size = 50 }: CustomerQuery): Promise<CustomerPage> => {
    await wait(240)
    const term = q?.trim().toLowerCase() ?? ''
    const matches = customers
      .filter((customer) => !stage || customer.stage === stage)
      .filter(
        (customer) =>
          !term ||
          [customer.firstName, customer.lastName, customer.email, customer.phone].some((value) =>
            value?.toLowerCase().includes(term),
          ),
      )
      .sort(
        (a, b) =>
          (a.lastName ?? '').localeCompare(b.lastName ?? '', 'de') ||
          (a.firstName ?? '').localeCompare(b.firstName ?? '', 'de'),
      )

    return {
      items: matches
        .slice(page * size, page * size + size)
        .map((customer) =>
          toListItem({ ...customer, ...balanceOf(demoBilling.creditsOf(customer.id)) }),
        ),
      page,
      size,
      total: matches.length,
    }
  },

  fetchDetail: async (customerId: string): Promise<CustomerDetail> => {
    await wait(240)
    return withCardBlocks(structuredClone(find(customerId)))
  },

  /** A fresh contact has bought nothing yet, so it opens at the top of the book. */
  create: async (input: CreateCustomerInput): Promise<CustomerDetail> => {
    await wait(320)

    const customer: CompleteCustomer = {
      id: String(Math.max(...customers.map((existing) => Number(existing.id))) + 1),
      name: input.name,
      firstName: input.name.split(' ')[0],
      lastName: input.name.split(' ').slice(1).join(' ') || input.name,
      createdAt: new Date().toISOString(),
      email: input.email,
      // Whichever number they gave; the book only has room for one.
      phone: input.phone || input.mobile,
      mobile: input.mobile || input.phone,
      city: input.city,
      country: input.country,
      language: input.language,
      company: `Abnehmen im Liegen ${input.city || CITIES[0]}`,
      tags: [],
      segment: 'Standard',
      bodyType: input.bodyType,
      programPlan: input.programPlan,
      documentCount: 0,
      manager: MANAGERS[0],
      lastActivityKind: 'Keine Aktivität',
      openActivities: 0,
      lastTreatment: '',
      saleCount: 0,
      signatureCount: 0,
      anamnesisSigned: false,
      treatments: [],
      documents: [],
      notes: [],
      activities: [
        {
          id: `${Date.now()}-a1`,
          kind: 'system',
          title: 'Kontakt erstellt',
          detail: 'Manuell im AiL Studio Workspace',
          at: 'Gerade eben',
        },
      ],
    }

    customers.unshift(customer)
    return structuredClone(customer)
  },

  /** Only the contact fields are editable; the rest comes from the backend. */
  update: async (customerId: string, input: CreateCustomerInput): Promise<CustomerDetail> => {
    await wait(320)

    const customer = find(customerId)
    Object.assign(customer, {
      name: input.name,
      email: input.email,
      phone: input.phone || input.mobile,
      mobile: input.mobile || input.phone,
      city: input.city,
      country: input.country,
      language: input.language,
      bodyType: input.bodyType,
      programPlan: input.programPlan,
    } satisfies Partial<CustomerDetail>)

    return structuredClone(customer)
  },

  /** DEMO-ONLY twice over: a stand-in for an endpoint that is itself a stand-in. */
  grantCredits: async (customerId: string, input: GrantCreditInput): Promise<CreditGrant> => {
    await wait(320)
    demoBilling.grantCredits(customerId, input.quantity)

    return {
      customerPublicId: customerId,
      granted: {
        quantity: input.quantity,
        note: input.note,
        grantedAt: new Date().toISOString(),
        byUserName: 'Admin',
      },
      credits: demoBilling.creditsOf(customerId),
      appointmentsChanged: [],
    }
  },

  /**
   * Activities, appointments and sales are separate records, not part of the
   * customer record the book shows — so in demo mode they simply succeed, and
   * the toast is the confirmation.
   */
  recordAction: async (): Promise<void> => {
    await wait(320)
  },
}
